import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FiX, FiArrowRight, FiCheck } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { Tour, TourPlacement, getTour, tourForPath } from './tours';
import { isTourSeen, markTourSeen, onStartTour } from './tourStore';
import { toast } from '../Toast';

// Guided tour overlay (spec 02 Dashboard / 02b Analytics – Tour step n). Mounted once by the v2 Header; renders into
// document.body above the header and modals. Registry + how to add a tour: tours.ts. Styles: Tour.scss (.v2-tour).
// - auto-starts the page's tour the first time a user opens it, once the first step's target is on the page
// - startTour(id) (tourStore.ts) starts any tour, navigating to its page first (tour.findPath when the url needs an id)
// - finishing, skipping, closing (✕ / Esc) or leaving the page marks the tour seen

const PAD = 8; // spotlight padding around the target
const GAP = 16; // spotlight → callout
const EDGE = 16; // callout ↔ viewport edge
const MOBILE = 640; // at or below: the callout docks to the bottom of the screen
const WAIT_MS = 10000; // how long to wait for a tour's first target

interface Box { top: number; left: number; width: number; height: number }

const findTarget = (target: string) => {
    const el = document.querySelector(`[data-tour="${target}"]`) as HTMLElement | null;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? el : null; // hidden at this width (e.g. the collapsed nav) = missing
};

const spotlightBox = (el: HTMLElement): Box => {
    const r = el.getBoundingClientRect();
    return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
};

const OPPOSITE: Record<TourPlacement, TourPlacement> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

// preferred side first, then the others; null when the callout fits nowhere (it then docks to the bottom)
const placeCallout = (s: Box, prefer: TourPlacement, w: number, h: number, vw: number, vh: number) => {
    const order = [prefer, OPPOSITE[prefer], 'bottom', 'top', 'right', 'left'] as TourPlacement[];
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));
    for (const p of order) {
        if (p === 'left' || p === 'right') {
            const left = p === 'right' ? s.left + s.width + GAP : s.left - GAP - w;
            if (left < EDGE || left + w > vw - EDGE) continue;
            return { left, top: clamp(s.top + PAD, EDGE, vh - h - EDGE) };
        }
        const top = p === 'bottom' ? s.top + s.height + GAP : s.top - GAP - h;
        if (top < EDGE || top + h > vh - EDGE) continue;
        return { top, left: clamp(s.left + PAD, EDGE, vw - w - EDGE) };
    }
    return null;
};

// brings the target (and room for the callout) into view; leaves the page alone when it already fits
const scrollToTarget = (el: HTMLElement, placement: TourPlacement, calloutHeight: number) => {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const docked = window.innerWidth <= MOBILE;
    const room = calloutHeight + GAP + PAD + EDGE;
    const visible = r.top >= EDGE + PAD && r.bottom <= vh - EDGE - PAD - (docked ? calloutHeight : 0);
    const fits = placement === 'top' ? r.top >= room : placement === 'bottom' ? r.bottom + room <= vh : true;
    if (visible && fits) return;
    let wantTop = EDGE + PAD;
    if (!docked && placement === 'top') wantTop = room;
    if (!docked && (placement === 'left' || placement === 'right') && r.height < vh - 2 * (EDGE + PAD)) wantTop = (vh - r.height) / 2;
    window.scrollTo({ top: Math.max(0, window.scrollY + r.top - wantTop), behavior: 'smooth' });
};

const TourHost: React.FC = () => {
    const username = useSelector((state: IGlobalState) => state.user.username);
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const history = useHistory();
    const { pathname } = useLocation();
    const [active, setActive] = useState<{ tour: Tour; step: number } | null>(null);
    // a tour waiting for its page / first target; manual = asked for from the help panel
    const [pending, setPending] = useState<{ id: string; manual: boolean } | null>(null);
    const [spot, setSpot] = useState<Box | null>(null);
    const [size, setSize] = useState({ w: 400, h: 280 });
    const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });
    const calloutRef = useRef<HTMLDivElement>(null);
    const primaryRef = useRef<HTMLButtonElement>(null);

    const step = active ? active.tour.steps[active.step] : null;

    const close = useCallback(() => {
        setActive(a => {
            if (a) markTourSeen(username, a.tour.id);
            return null;
        });
    }, [username]);

    useEffect(() => onStartTour(id => { if (getTour(id)) setPending({ id, manual: true }); }), []);

    // page change: a running tour that doesn't belong here ends (seen); a page with an unseen tour queues it
    useEffect(() => {
        if (active && !active.tour.match(pathname)) close();
        const tour = tourForPath(pathname);
        if (tour && !isTourSeen(username, tour.id)) setPending(p => p ?? { id: tour.id, manual: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, username]);

    // pending → go to its page, wait for the first target, start
    useEffect(() => {
        if (!pending) return;
        const tour = getTour(pending.id);
        if (!tour) { setPending(null); return; }
        if (!tour.match(pathname)) {
            if (!pending.manual) { setPending(null); return; } // auto tour for a page we've left
            if (!tour.findPath) { history.push(tour.path); return; }
            // a page whose url needs an id (holder detail): look one up, then go there; none → its fallback page
            let live = true;
            const find = async () => {
                let found: string | null = null;
                if (stock) found = await tour.findPath!(stock);
                if (!live) return;
                if (found) { history.push(found); return; }
                setPending(null);
                toast.info(tour.notFound ?? 'There is nothing to show this tour on yet.');
                history.push(tour.path);
            };
            find();
            return () => { live = false; };
        }
        if (active?.tour.id === tour.id && !pending.manual) { setPending(null); return; }
        const started = Date.now();
        const timer = window.setInterval(() => {
            const ready = !!findTarget(tour.steps[0].target);
            const waited = Date.now() - started > WAIT_MS;
            // auto: only once the page has rendered its first target (else try again next visit);
            // manual: start anyway after the wait, the missing step shows centred
            if (ready || (waited && pending.manual)) {
                setActive({ tour, step: 0 });
                setPending(null);
            } else if (waited) {
                setPending(null);
            }
        }, 200);
        return () => window.clearInterval(timer);
    }, [pending, pathname, history, active, stock]);

    // track the target: scroll it into view on each step, then follow scroll / resize / late layout changes
    useEffect(() => {
        if (!step) return;
        const el = findTarget(step.target);
        if (el) scrollToTarget(el, step.placement, calloutRef.current?.offsetHeight ?? 280);
        let frame = 0;
        const measure = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const t = findTarget(step.target);
                setSpot(t ? spotlightBox(t) : null);
                setViewport({ w: window.innerWidth, h: window.innerHeight });
            });
        };
        measure();
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        const poll = window.setInterval(measure, 400); // data loading, carousels, charts resizing
        return () => {
            cancelAnimationFrame(frame);
            window.clearInterval(poll);
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
    }, [step]);

    // the callout's 400px size decides where it fits; a docked callout is full-width and shorter, so measuring it
    // there would flip it back to anchored and loop — only measure the undocked box, and ignore sub-pixel changes
    useLayoutEffect(() => {
        const c = calloutRef.current;
        if (!c || c.classList.contains('is-docked')) return;
        if (Math.abs(c.offsetWidth - size.w) > 1 || Math.abs(c.offsetHeight - size.h) > 1) setSize({ w: c.offsetWidth, h: c.offsetHeight });
    });

    useEffect(() => { if (step) primaryRef.current?.focus({ preventScroll: true }); }, [step]);

    const go = useCallback((delta: number) => {
        setActive(a => {
            if (!a) return a;
            const next = a.step + delta;
            if (next < 0) return a;
            if (next >= a.tour.steps.length) {
                markTourSeen(username, a.tour.id);
                return null;
            }
            return { ...a, step: next };
        });
    }, [username]);

    useEffect(() => {
        if (!active) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
            else if (e.key === 'ArrowRight') go(1);
            else if (e.key === 'ArrowLeft') go(-1);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [active, close, go]);

    if (!active || !step) return null;

    const { tour } = active;
    const index = active.step;
    const last = index === tour.steps.length - 1;
    const docked = viewport.w <= MOBILE;
    const pos = spot && !docked ? placeCallout(spot, step.placement, size.w, size.h, viewport.w, viewport.h) : null;
    // no target → centred; no room beside it (or mobile) → docked to the bottom
    const mode = !spot ? 'is-centred' : pos ? 'is-anchored' : 'is-docked';
    const tag = `${tour.label ? `${tour.label} · ` : ''}Step ${index + 1} of ${tour.steps.length}`;

    return ReactDOM.createPortal(<div className="v2-tour">
        {/* catches clicks so the page can't be used mid-tour; Esc / ✕ / Skip always close it */}
        <div className={`tour-blocker${spot ? '' : ' is-dim'}`} />
        {spot && <div className="tour-spotlight" style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }} />}
        <div ref={calloutRef} className={`tour-callout ${mode}`} role="dialog" aria-modal="true" aria-labelledby="tour-title"
            style={pos ? { top: pos.top, left: pos.left } : undefined}>
            <div className="tour-callout-head">
                <span className="tour-tag">{tag}</span>
                <button type="button" className="tour-close" aria-label="Skip tour" title="Skip tour" onClick={close}><FiX /></button>
            </div>
            <h2 id="tour-title" className="tour-title">{step.title}</h2>
            <p className="tour-body">{step.body}</p>
            {step.points && <ul className="tour-points">{step.points.map(p => <li key={p}>{p}</li>)}</ul>}
            {step.tip &&<p className="tour-tip">{step.tip}</p>}
            <div className="tour-foot">
                <div className="tour-dots" aria-hidden="true">
                    {tour.steps.map((_, i) => <span key={i} className={`tour-dot${i < index ? ' is-done' : i === index ? ' is-current' : ''}`} />)}
                </div>
                {index === 0
                    ? <button type="button" className="tour-btn tour-skip" onClick={close}>Skip Tour</button>
                    : <button type="button" className="tour-btn tour-back" onClick={() => go(-1)}>Back</button>}
                <button ref={primaryRef} type="button" className="tour-btn tour-next" onClick={() => go(1)}>
                    {last ? <><FiCheck /> Finish</> : <><FiArrowRight /> Next</>}
                </button>
            </div>
        </div>
    </div>, document.body);
};

export default TourHost;
